import { datadogLogs } from '@datadog/browser-logs';
import _ from 'lodash';
import { fork, takeLatest, put, call, select } from 'redux-saga/effects';
import { v4 } from 'uuid';

import { getSplitTestValue } from '@src/utils/selectors';

import {
    ACTIVATE_TRADING_HISTORY,
    TRADING_HISTORY_LOADING,
    TRADING_HISTORY_READY,
    TRADING_HISTORY_ERROR,
    UPLOAD_DOCS,
    UPLOAD_DOCS_SUCCESS,
    UPLOAD_DOCS_ERROR,
    UPLOAD_DOCS_LOADING,
    REMOVE_UPLOAD_DOCS_ALERT,
    TRADING_HISTORY_DOC_STATUS,
    UPDATE_TRADING_HISTORY_DOC_STATUS,
    HANDLE_TRADING_HISTORY_DOC_SUBMIT,
    FETCH_UPLOADED_DOCS,
    CUSTOMER_CONFIRMED_DOCS,
    CUSTOMER_CONFIRMED_DOCS_ERROR,
    CUSTOMER_CONFIRMED_DOCS_SUCCESS,
    HANDLE_TRUE_LAYER_LINKED,
    TRUELAYER_LINKED_LOADING,
    TRUELAYER_LINKED_COMPLETE,
    TRUELAYER_LINKED_ERROR
} from './actions';
import { amplitudeDocUploadEvent } from './TradingHistory.amplitude';
import { getApplicationSuggestedProduct } from './TradingHistory.helpers';
import {
    fetchUploadedDocumentsEdge,
    postCustomerConfirmationProvidingDocs,
    postSaveUploadedDocumentsEdge
} from '../../lendingAPI/lendingAPI';
import { putAPI } from '../../lendingAPI/lendingAPIHelpers';
import { getCountryCode } from '../../lendingAPI/lendingAPISelector';
import { saveCustomerState } from '../AutomatedLendingDecision/AutomatedLendingDecisionUtils';
import { GET_LOAN_DECISION } from '../LoanDecision/LoanDecision.actions';
import { handleDecision } from '../LoanDecision/LoanDecision.sagas';

export default [
    fork(tradingHistoryViewWorker),
    fork(uploadDocuments),
    fork(tradingHistorySubmitDocs),
    fork(trueLayerLinked)
];

const delay = (time) => new Promise((resolve) => setTimeout(resolve, time));

function* tradingHistoryViewWorker() {
    yield takeLatest(ACTIVATE_TRADING_HISTORY, function* () {
        const stateKey = yield select(
            ({ lendingAPI }) =>
                lendingAPI.impersonatingStateKey || lendingAPI.stateKey
        );

        yield put({ type: TRADING_HISTORY_LOADING });

        try {
            const { countryCode } = yield select((state) => ({
                countryCode: getCountryCode(state)
            }));

            yield putAPI({ type: 'api/GET_APPLICATION' });

            yield saveCustomerState('businessAccounts');

            const { suggestedProduct, loanSummaryProduct } =
                yield call(refreshOffers);

            yield* runLoanDecision();

            yield put({
                type: TRADING_HISTORY_READY,
                selectedOffers: suggestedProduct,
                loanSummary: loanSummaryProduct
            });

            if (countryCode === 'uk') {
                yield putAPI({
                    type: 'api/GET_FORMAL_INDICATIVE_OFFERS'
                });
            }

            const { data } = yield call(fetchUploadedDocumentsEdge, stateKey);

            yield putAPI({
                type: FETCH_UPLOADED_DOCS,
                payload: data
            });
        } catch (error) {
            yield put({
                type: TRADING_HISTORY_ERROR,
                error: error
            });
        }
    });
}

function* tradingHistorySubmitDocs() {
    yield takeLatest(HANDLE_TRADING_HISTORY_DOC_SUBMIT, function* () {
        try {
            const {
                offersResponse,
                uploadedFiles,
                isBusinessDetailsReductionSplitTest
            } = yield select((state) => {
                return {
                    offersResponse: state?.lendingAPI?.offers,
                    uploadedFiles: state?.tradingHistory?.uploadedFiles,
                    isBusinessDetailsReductionSplitTest: getSplitTestValue(
                        state,
                        'business_details_reduction'
                    )
                };
            });

            const selectedOffers = offersResponse?.data?.offers[0];

            const requirementCategory =
                selectedOffers?.data_requirements.filter(
                    (dataRequirement) =>
                        dataRequirement.category === 'documents'
                );

            const requiredDocs = requirementCategory[0]?.actions.filter(
                (action) => {
                    return (
                        action.status === 'required' &&
                        action.is_optional === false
                    );
                }
            );

            const uploadedDocTypes = uploadedFiles.map((doc) =>
                doc.type === 'business_accounts' ? 'company_accounts' : doc.type
            );

            let missingDocs = requiredDocs
                .filter((doc) => !uploadedDocTypes.includes(doc.type))
                .map((doc) => doc.type);

            const isDocumentVATRequired = requiredDocs.some(
                (doc) => doc.type === 'vat_return'
            );

            if (isBusinessDetailsReductionSplitTest && !isDocumentVATRequired) {
                yield call(
                    handleVATSwitchRequirement,
                    uploadedDocTypes,
                    missingDocs
                );
            }

            if (missingDocs && missingDocs.length > 0) {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            }

            yield put({
                type: TRADING_HISTORY_DOC_STATUS,
                missing: missingDocs
            });

            if (missingDocs.length === 0) {
                yield call(initiateCustomerConfirmationProvidingDocs);

                yield put({
                    type: GET_LOAN_DECISION
                });
            }
        } catch (error) {
            yield put({
                type: TRADING_HISTORY_ERROR,
                error: error
            });
        }
    });
}

function* handleVATSwitchRequirement(uploadedDocs, missingDocs) {
    const vatToggle = yield select((state) => state?.tradingHistory.vatToggle);

    const hasUploadedVATDocs = uploadedDocs.includes('vat_return');

    if (!vatToggle || hasUploadedVATDocs) {
        return missingDocs.filter((doc) => doc !== 'vat_return');
    }

    if (vatToggle && !hasUploadedVATDocs) {
        return missingDocs.push('vat_return');
    }
}

function* refreshOffers() {
    const application_id = yield select(
        ({ lendingAPI }) => lendingAPI.application.data.application_id
    );

    const { payload: offersResponsePayload } = yield putAPI({
        type: 'api/GET_OFFERS',
        body: { application_id }
    });

    const selectedOffer = offersResponsePayload?.offers[0];

    const state = yield select();

    const loanSummaryProduct = getApplicationSuggestedProduct(state);

    return {
        suggestedProduct: selectedOffer,
        loanSummaryProduct
    };
}

function* uploadDocuments() {
    yield takeLatest(UPLOAD_DOCS, function* (action) {
        try {
            yield [...action.documents].map((document) =>
                call(uploadSingleFile, document, action.documentType)
            );
        } catch (error) {
            yield put({
                type: TRADING_HISTORY_ERROR,
                error: error
            });
        }
    });
}

function* runLoanDecision() {
    const { offersResponse } = yield select((state) => {
        return {
            offersResponse: state?.lendingAPI?.offers
        };
    });

    const offerCreationStatus = offersResponse?.data?.offer_creation_status;
    const selectedOffer = offersResponse?.data?.offers[0];

    const hasDecision =
        offerCreationStatus === 'declined' ||
        offerCreationStatus === 'deferred' ||
        selectedOffer.offer_type === 'confirmed';

    if (hasDecision) {
        return yield handleDecision(offerCreationStatus === 'declined');
    }
}

function* uploadSingleFile(document, documentType) {
    const stateKey = yield select(
        ({ lendingAPI }) =>
            lendingAPI.impersonatingStateKey || lendingAPI.stateKey
    );

    const body = new FormData();

    body.append('document', document);
    body.append('document_type', documentType);

    const { name } = body.get('document');

    yield put({
        type: UPLOAD_DOCS_LOADING,
        payload: {
            id: null,
            type: _.snakeCase(documentType),
            attributes: {
                document_name: name,
                document_type: documentType
            },
            loading: true
        }
    });
    const tempId = v4();
    const response = yield call(postSaveUploadedDocumentsEdge, stateKey, body);

    if (response.errors) {
        yield put({
            type: UPLOAD_DOCS_ERROR,
            payload: {
                id: null,
                attributes: {
                    document_id: tempId,
                    document_name: name,
                    document_type: documentType
                },
                error: response.errors[0]
            }
        });

        yield call(delay, 5000);
        yield put({
            type: REMOVE_UPLOAD_DOCS_ALERT,
            document_id: tempId
        });
    } else {
        amplitudeDocUploadEvent(documentType);

        yield put({
            type: UPLOAD_DOCS_SUCCESS,
            payload: {
                id: response.data.document_id,
                type: _.snakeCase(response.data.document_type),
                attributes: {
                    document_id: response.data.document_id,
                    document_name: response.data.document_name,
                    document_type: response.data.document_type
                }
            }
        });

        yield put({
            type: UPDATE_TRADING_HISTORY_DOC_STATUS
        });

        const { suggestedProduct, loanSummaryProduct } =
            yield call(refreshOffers);

        yield put({
            type: TRADING_HISTORY_READY,
            selectedOffers: suggestedProduct,
            loanSummary: loanSummaryProduct
        });

        yield call(delay, 5000);
        yield put({
            type: REMOVE_UPLOAD_DOCS_ALERT,
            document_id: response.data.document_id
        });
    }
}

function* initiateCustomerConfirmationProvidingDocs() {
    yield put({ type: CUSTOMER_CONFIRMED_DOCS });
    const stateKey = yield select(
        ({ lendingAPI }) =>
            lendingAPI.impersonatingStateKey || lendingAPI.stateKey
    );

    const body = {
        data: {
            customer_confirmed_providing_docs: true,
            timestamp: new Date().toISOString()
        }
    };

    try {
        yield call(postCustomerConfirmationProvidingDocs, stateKey, body);
        yield put({ type: CUSTOMER_CONFIRMED_DOCS_SUCCESS });
    } catch (error) {
        yield put({ type: CUSTOMER_CONFIRMED_DOCS_ERROR });
        datadogLogs.logger.error({
            message:
                'customer_confirmation_providing_docs failed to submit post request',
            error
        });
    }
}

function* trueLayerLinked() {
    yield takeLatest(HANDLE_TRUE_LAYER_LINKED, function* () {
        try {
            yield put({ type: TRUELAYER_LINKED_LOADING });

            const { suggestedProduct, loanSummaryProduct } =
                yield call(refreshOffers);

            yield put({
                type: TRADING_HISTORY_READY,
                selectedOffers: suggestedProduct,
                loanSummary: loanSummaryProduct
            });

            yield put({ type: TRUELAYER_LINKED_COMPLETE });
        } catch (error) {
            yield put({ type: TRUELAYER_LINKED_ERROR, error });
            datadogLogs.logger.error(
                'Fetch GET /application/{state_key}/offers/ error',
                {
                    error: error.message
                }
            );
        }
    });
}
